import { useCallback, useEffect } from 'react';
import type { WidthKey } from './constants';
import { usePanelContext } from './context';
import { GlobalPanel } from './helpers';
import type { PanelConfig } from './PANEL-CONFIG';
import { usePanelStore } from './store';
/**
 * A hook that returns the current width of the current panel (within a PanelPortal).
 *
 * This hook uses the panel context and panel store to retrieve the current width
 * of the specific panel identified by its ID.
 *
 * @returns {WidthKey} The current width key of the panel
 */
export function useCurrentPanelWidth(): WidthKey {
  const { id } = usePanelContext();
  return usePanelStore((state) => {
    return state.currentPanelWidths[id];
  });
}

/**
 * A hook that returns the mount target id of the current panel (within a PanelPortal).
 *
 * @returns {string} The mount target id of the panel
 */
export function useCurrentPanelMountTargetId(): string {
  const { mountTargetId } = usePanelContext();
  return mountTargetId;
}

/**
 * A hook that returns a function to update the width of the current panel (within a PanelPortal).
 *
 * @returns A function that accepts a WidthKey parameter to set the new width of the current panel.
 *
 * @example
 * ```tsx
 * const updateWidth = useUpdateCurrentPanelWidth();
 * updateWidth('lg'); // Updates the current panel's width to large
 * ```
 */
export function useUpdateCurrentPanelWidth(): (width: WidthKey) => void {
  const { id } = usePanelContext();
  const updatePanelWidth = usePanelStore((state) => state.updatePanelWidth);

  return (width: WidthKey) => {
    updatePanelWidth(id, width);
  };
}

/**
 * A hook that returns the width of a specific panel identified by its ID.
 * It can be used to retrieve the width of any panel that's registered in the panel store.
 *
 * @param id The ID of the panel
 *
 * @returns {WidthKey} The current width key of the panel
 */
export const useGetPanelWidth = (id: PanelConfig) => {
  return usePanelStore((state) => {
    return state.currentPanelWidths[id];
  });
};

/**
 * A hook that returns a function to update the width of a specific panel identified by its ID.
 *
 * @param id The ID of the panel
 *
 * @returns A function that accepts a WidthKey parameter to set the new width of the panel.
 *
 * @example
 * ```tsx
 * const updateWidth = useUpdatePanelWidth('my-panel-id');
 * updateWidth('lg'); // Updates the panel's width to large
 * ```
 */
export const useUpdatePanelWidth = (id: PanelConfig) => {
  const updatePanelWidth = usePanelStore((state) => state.updatePanelWidth);
  return (width: WidthKey) => {
    updatePanelWidth(id, width);
  };
};

/**
 * Custom hook to manage the state and actions for a specific global panel.
 *
 * This hook subscribes to the `usePanelStore` to get the current open state
 * of the panel identified by the provided `id`. It also provides memoized
 * callback functions to open and close the panel using the `GlobalPanel` utility.
 *
 * @param id - The configuration ID (`PanelConfig`) of the global panel to manage.
 * @returns An object containing:
 *   - `isOpen`: A boolean indicating whether the specified panel is currently open.
 *   - `close`: A memoized function to close the specified panel by calling `GlobalPanel.close`.
 *   - `open`: A memoized function to open the specified panel by calling `GlobalPanel.open`.
 *     (Note: The original code sample calls `GlobalPanel.close` in the `open` function,
 *      this documentation assumes the intent was to call `GlobalPanel.open`).
 *
 * @example
 * ```tsx
 * import { useGlobalPanel } from './hooks';
 * import { PanelConfig } from './types'; // Assuming PanelConfig is defined elsewhere
 *
 * const MyComponent = () => {
 *   const { isOpen, open, close } = useGlobalPanel('call-takeaway');
 *
 *   return (
 *     <div>
 *       <button onClick={open}>Open Settings</button>
 *       {isOpen && (
 *         <div>
 *           Panel Content
 *           <button onClick={close}>Close</button>
 *         </div>
 *       )}
 *     </div>
 *   );
 * };
 * ```
 */
export const useGlobalPanel = (id: PanelConfig) => {
  const isOpen = !!usePanelStore((state) => state.openGlobalPanels[id]);

  const close = useCallback(() => {
    GlobalPanel.close(id);
  }, [id]);

  const open = useCallback(() => {
    GlobalPanel.open(id);
  }, [id]);

  return { close, isOpen, open };
};

/**
 * Custom hook to manage the force tray functionality.
 *
 * This hook provides access to the current force tray state and functions to control it.
 * When force tray is enabled, all panels will be forced to open in the tray regardless
 * of available space. The force tray mode is automatically disabled when all panels are closed.
 *
 * @returns An object containing:
 *   - `forceTray`: A boolean indicating whether force tray mode is currently active.
 *   - `setForceTray`: A function to enable or disable force tray mode.
 *   - `enableForceTray`: A convenience function to enable force tray mode.
 *   - `disableForceTray`: A convenience function to disable force tray mode.
 *
 * @example
 * ```tsx
 * import { useForceTray } from './hooks';
 *
 * const MyComponent = () => {
 *   const { forceTray, enableForceTray, disableForceTray } = useForceTray();
 *
 *   return (
 *     <div>
 *       <p>Force Tray Mode: {forceTray ? 'Enabled' : 'Disabled'}</p>
 *       <button onClick={enableForceTray}>Enable Force Tray</button>
 *       <button onClick={disableForceTray}>Disable Force Tray</button>
 *     </div>
 *   );
 * };
 * ```
 */
export const useForceTray = () => {
  const forceTray = usePanelStore((state) => state.forceTray);
  const setForceTray = usePanelStore((state) => state.setForceTray);

  const enableForceTray = useCallback(() => {
    setForceTray(true);
  }, [setForceTray]);

  const disableForceTray = useCallback(() => {
    setForceTray(false);
  }, [setForceTray]);

  return {
    forceTray,
    setForceTray,
    enableForceTray,
    disableForceTray,
  };
};

/**
 * A hook to manage the height offset for the panel layout.
 *
 * This hook allows you to subscribe to changes in the height offset,
 * which can be useful for adjusting the layout based on dynamic content
 * or other factors that affect the height of the panel.
 *
 * @param offset The height offset to apply.
 */
export function useHeightOffset(offset: number) {
  useEffect(() => {
    usePanelStore.setState((state) => ({
      heightOffset: state.heightOffset + offset,
    }));

    return () => {
      usePanelStore.setState((state) => ({
        heightOffset: state.heightOffset - offset,
      }));
    };
  }, [offset]);
}
